import React, {
  useCallback,
  useEffect,
  useMemo,
  useState,
} from 'react';
import { useDispatch, useSelector } from 'react-redux';
import {
  Row,
  Col,
  List,
  notification,
  message,
  Spin,
} from 'antd';
import { AutoBreak, TaskHelpers } from 'ontraccr-common';
import PropTypes from 'prop-types';
import { DateTime } from 'luxon';

import FullPhoto from '../../common/photos/FullPhoto';

import ManualEntryFormDrawer from './ManualEntryFormDrawer';
import HoursRedistributionDrawer from '../../timecards/Summary/HoursRedistributionDrawer';
import ManualEntryCard from './ManualEntryCard';
import ManualEntryFooter from './ManualEntryFooter';
import ManualEntrySignature from './ManualEntrySignature';

import {
  loadManualEntryData,
  addOrUpdateEntries,
  prepareManualEntryPayload,
  foundTaskConflict,
  loadTasks,
} from './manualEntryHelpers';

import { updateCostcodeIdMapForTasks } from '../../costcodes/state/costcodes.actions';

import { getApprovals } from '../state/clockin.actions';
import { getCustomFields, getSignatureURL } from '../../timecards/state/timecards.actions';
import { getUsers } from '../../users/state/users.actions';
import { getCustomerLabels, getCustomers } from '../../contacts/customers/state/customers.actions';
import { getEquipment } from '../../equipment/state/equipment.actions';
import { getMaterials } from '../../materials/state/materials.actions';
import { getCustomTables } from '../../forms/state/forms.actions';
import TimeTracking from '../../state/timeTracking';

import { getIdMap } from '../../helpers/helpers';
import { downloadFile, getFileType } from '../../files/fileHelpers';
import {
  prepareResponsePayload,
} from '../../forms/ResponderHelpers';
import ManualEntryLocation from './ManualEntryLocation';
import { onNextFileClick } from '../../helpers/fileHelpers';
import { TIME_TYPE_FLAG } from '../../constants/Flags';
import CustomConfirmModal from '../../common/modals/CustomConfirmModal';
import { hasEditedTimes } from '../../timecards/timecard.helpers';
import Analytics from '../../helpers/Analytics';
import Permissions from '../../auth/Permissions';

import { decorateShallowFile } from '../../forms/formHelpers';
import { FOOTER_HEIGHT } from '../../constants/Styles';

const AUTO_BREAK_INCLUDE_KEYS = [ // priority removal order DT OT, OT, regular
  {
    endTimeKey: 'doubleOTEndTime',
    startTimeKey: 'doubleOTStartTime',
  },
  {
    endTimeKey: 'otEndTime',
    startTimeKey: 'otStartTime',
  },
  {
    endTimeKey: 'endTime',
    startTimeKey: 'startTime',
  },
];

const mergeDecoratedFiles = (target, source) => {
  if (!source) return;
  Object.keys(source).forEach((fileId) => {
    target[fileId] = decorateShallowFile(source[fileId], fileId);
  });
};

function ManualEntry({
  user = {},
  onClose,
  visible,
  personal,
  initialEntries,
  defaultDate,
  isDisplay,
  onEditClicked,
  canEditApproved,
  canEditUnapproved,
  showFormDrawer,
  setShowFormDrawer,
  day,
  isApprovals = false,
  isSummary,
  updateManualEntryData,
  isCopying,
  setIsCopying,
  setIsLoading,
  isRedistributingHours,
  setIsRedistributingHours,
  selectedSummaryTask,
}) {
  const {
    classId: defaultClassId,
  } = user;

  const dispatch = useDispatch();

  const { classes = [] } = useSelector((state) => state.unions);
  const profile = useSelector((state) => state.profile.profile);
  const phases = useSelector((state) => state.costcodes.phases);
  const {
    settings = {},
  } = useSelector((state) => state.settings.company || {});
  const customFields = useSelector((state) => state.timecards.customFields);
  const timeEntryUserMap = useSelector((state) => state.timeTracking.timeEntryUserMap);
  const users = useSelector((state) => state.users.users);
  const projects = useSelector((state) => state.projects.projects);
  const equipment = useSelector((state) => state.equipment.equipment);
  const positions = useSelector((state) => state.settings.positions);
  const { paidFlags = [] } = useSelector((state) => state.settings.company);
  const mainLoaded = useSelector((state) => (state.main.loaded));
  const currentTimeEntriesRange = useSelector((state) => state.timecards.currentTimeEntriesRange);
  const globalFileMap = useSelector((state) => state.files.fileMap);

  const paidFlagSet = new Set(paidFlags);
  const hasFutureDayPermission = Permissions.has('MULTI_DAY_FUTURE_MANUAL_ENTRY');

  const {
    enableHourBasedTracking = false,
    disableAutoBreakEditPrompt = false,
  } = settings;

  const userMap = useMemo(() => getIdMap(users), [users]);
  const projectIdMap = useMemo(() => getIdMap(projects), [projects]);
  const equipmentIdMap = useMemo(() => getIdMap(equipment), [equipment]);

  const userId = user?.id ?? profile?.id;
  const tasks = timeEntryUserMap[userId] ?? [];

  const [entries, setEntries] = useState([]);
  const [selectedEntry, setSelectedEntry] = useState();
  const [loading, setLoading] = useState(false);
  const [customDataLoading, setCustomDataLoading] = useState(false);
  const [selectedFile, setSelectedFile] = useState();
  const [selectedFileDetails, setSelectedFileDetails] = useState();
  const [fileMap, setFileMap] = useState({});
  const [locationDrawerConfig, setLocationDrawerConfig] = useState();
  const [signatureUrl, setSignatureURL] = useState();
  const [hasLoadedCustomData, setHasLoadedCustomData] = useState(false);
  const [hasLoadedSummaryTask, setHasLoadedSummaryTask] = useState(false);
  const [deletedIds, setDeletedIds] = useState(new Set());
  const [initialLoaded, setInitialLoaded] = useState(false);
  const [customDataMap, setCustomDataMap] = useState();

  const globalFile = globalFileMap[selectedFile?.id];

  const onCloseLocationDrawer = useCallback(() => setLocationDrawerConfig(), []);

  const hideFormDrawer = useCallback(() => {
    setShowFormDrawer(false);
    setSelectedEntry();
    setIsCopying(false);
  }, []);
  const onFileClose = useCallback(() => {
    setSelectedFile();
    setSelectedFileDetails();
  }, []);
  const hideRedistributionDrawer = useCallback(() => {
    setIsRedistributingHours(false);
    hideFormDrawer();
  }, []);

  const getWithinRangeEntriesMap = useCallback(async (taskDates, taskUsers) => {
    const minDate = DateTime.min(...taskDates);
    const maxDate = DateTime.max(...taskDates);
    const [startDT, endDT] = currentTimeEntriesRange;

    let ourTimeEntryUserMap = timeEntryUserMap;
    if (!minDate || !maxDate) return ourTimeEntryUserMap;

    if (!(minDate >= startDT && maxDate <= endDT) || isApprovals) {
      const outsideOfRangeEntries = await loadTasks({
        startTime: minDate.startOf('day').toMillis(),
        endTime: maxDate.endOf('day').toMillis(),
        userIds: taskUsers,
      });

      ourTimeEntryUserMap = outsideOfRangeEntries.reduce((acc, entry) => {
        if (!entry.userId) return acc;
        if (!(entry.userId in acc)) {
          acc[entry.userId] = [];
        }
        acc[entry.userId].push(entry);
        return acc;
      }, {});
    }

    return ourTimeEntryUserMap;
  }, [timeEntryUserMap, currentTimeEntriesRange, isApprovals]);

  const askShouldDelete = useCallback(async () => new Promise((resolve) => {
    CustomConfirmModal({
      title: 'Delete Auto Break?',
      okText: 'Yes',
      cancelText: 'No',
      content: 'This timecard no longer meets the threshold for an Automatic Break. Would you like to delete the Automatic Break?',
      onOk: () => {
        resolve(true);
      },
      onCancel: () => {
        resolve(false);
      },
    });
  }), []);

  const onSubmit = useCallback(async () => {
    setLoading(true);

    const newEntries = entries.map((task) => {
      // If customData is an array, the user hasnt modified it
      // If customData is a response object we need to prepare for uploading
      if (!task.customData || Array.isArray(task.customData)) return task;
      const { divisionId } = task;
      const {
        [divisionId]: { fields: sections = [] } = {},
      } = customFields;
      const {
        responses: preparedResponses,
      } = prepareResponsePayload({
        sections,
        responses: task.customData,
      });
      return {
        ...task,
        customData: preparedResponses,
        isUpdated: true,
      };
    }).filter((e) => !e.isPending);

    const tasksByUserId = {};
    const userIds = new Set();
    newEntries.forEach((entry) => {
      userIds.add(entry.userId);
      if (!tasksByUserId[entry.userId]) tasksByUserId[entry.userId] = [];
      tasksByUserId[entry.userId].push(entry);
    });

    const taskDates = newEntries
      .map((t) => TaskHelpers.getTaskDate(t))
      .filter((date) => !!date);

    const ourTimeEntryUserMap = await getWithinRangeEntriesMap(taskDates, Array.from(userIds));

    const filteredInitialEntries = initialEntries.filter((e) => !e.isPending);

    const payloadTasks = TaskHelpers.getEntriesDiff({
      entries: newEntries,
      initialEntries: filteredInitialEntries.filter((entry) => !!TaskHelpers.getEndTime(entry)),
      // New entries have uuid that is like acfaeeb8-b766-419a-a099-082f01263fe3
      // Existing like d34cf3491624ca1a95a52a2897cecc8af3c2
      entryIsNew: (e) => e.id?.includes('-'),
      onFail: () => {
        notification.error({
          key: 'error',
          message: 'Error',
          description:
            'Unable to create tasks in the future, please change the clock-in/out times',
          duration: 5,
        });
      },
      keepId: true, // We need the ids for auto break merging
      hasFutureDayPermission,
    });

    const initialEntryIds = new Set(filteredInitialEntries.map(({ id }) => id));
    // eslint-disable-next-line no-restricted-syntax
    for (const taskUserId of userIds) {
      const ourUser = userMap[taskUserId];
      const userPermissions = new Set(positions[ourUser.position] || []);
      const ourTasks = ourTimeEntryUserMap[taskUserId] ?? [];
      const ourNewTasks = tasksByUserId[taskUserId];

      // Get the new set of user's tasks including their history, excluding running
      const ourFullEditedTasks = [];
      const deletedAutoBreaks = [];
      ourTasks.forEach((task) => {
        if (!initialEntryIds.has(task.id) && task.endTime) {
          ourFullEditedTasks.push(task);
        } else if (deletedIds.has(task.id)
          && task.endTime
          && task.type === 'break'
          && task.note === 'Automatic Break'
        ) {
          deletedAutoBreaks.push(task);
        }
      });
      ourFullEditedTasks.push(...ourNewTasks);

      const affectedDays = new Set();
      ourNewTasks.forEach((task) => {
        affectedDays.add(TaskHelpers.getTaskDate(task).toSQLDate());
      });

      // eslint-disable-next-line no-restricted-syntax
      for (const taskDay of affectedDays) {
        // eslint-disable-next-line no-await-in-loop
        const userAutoBreakDiff = await AutoBreak({
          settings,
          tasks: ourFullEditedTasks,
          userPermissions,
          notifyError: () => {
            notification.error({
              key: 'error',
              message: 'Error',
              description:
                `We were unable to add an automatic break for ${ourUser.name} on ${taskDay}. Please add one manually if applicable.`,
              duration: 5,
            });
          },
          day: DateTime.fromSQL(taskDay),
          askShouldDelete,
          deletedAutoBreaks,
          Analytics,
          hasFutureDayPermission,
        });

        TaskHelpers.mergeManualEntries(payloadTasks, userAutoBreakDiff);
      }
    }
    if (!payloadTasks) {
      setLoading(false);
      return;
    }
    const {
      tasksToEdit,
    } = payloadTasks;

    const payload = {
      payload: await prepareManualEntryPayload(payloadTasks),
      userId,
      isSubmitted: isApprovals,
      useEndDateOvernight: settings.useEndDateOvernight,
    };
    if (await dispatch(TimeTracking.manualEntry(payload))) {
      if (isApprovals) dispatch(getApprovals('manualEntry'));
      onClose();
      updateManualEntryData?.(tasksToEdit.map((task) => task.id));
    }
    setLoading(false);
  }, [
    entries,
    userId,
    personal,
    initialEntries,
    customFields,
    isApprovals,
    updateManualEntryData,
    settings,
    userMap,
    positions,
    getWithinRangeEntriesMap,
    hasFutureDayPermission,
  ]);

  const onSubmitEntry = useCallback(async (newTasks, hourBased) => {
    const conflictMessages = [];
    let parsedEntries = [];
    let hasSelectedEntryUser = false;

    await Promise.all(newTasks.map(async ({
      task,
      breakObject,
      overtime,
      pto,
      users: taskUsers,
    }) => {
      if (
        !task
        && !breakObject
        && !overtime
        && !taskUsers
        && !pto
      ) return;

      const taskDates = [
        TaskHelpers.getTaskDate(task),
        TaskHelpers.getTaskDate(breakObject),
        TaskHelpers.getTaskDate(overtime),
        TaskHelpers.getTaskDate(pto),
      ].filter((date) => !!date);

      const ourTimeEntryUserMap = await getWithinRangeEntriesMap(taskDates, taskUsers);

      const userConflictChecks = [];

      taskUsers.forEach((taskUserId) => {
        const splitFn = paidFlagSet.has(TIME_TYPE_FLAG)
          ? TaskHelpers.createTasksFromTaskAndBreak
          : TaskHelpers.createTasksFromTaskAndBreakOriginal;
        const splitEntries = !hourBased || !paidFlagSet.has(TIME_TYPE_FLAG)
          ? splitFn({
            task,
            breakObject,
          }) : [task];

        const fullEntries = splitEntries;

        if (!paidFlagSet.has(TIME_TYPE_FLAG)) {
          if (overtime) {
            fullEntries.push(overtime);
          }

          if (pto) {
            fullEntries.push(pto);
          }
        }

        let ourParsedEntries = entries.filter(({ userId: entryUserId }) => (
          taskUserId === entryUserId
        ));
        const editIds = new Set();

        fullEntries.forEach((newEntry) => {
          let selectedEntryToPass = isCopying ? null : selectedEntry;

          // This can only occur if the user is editing an existing entry
          // And adds an inline break that results in an entry being split into two
          // In this case we want to update the existing entry
          // And create a new entry for the split
          // The creation will occur when we pass a null selected entry
          if (
            selectedEntry
            && paidFlagSet.has(TIME_TYPE_FLAG)
            && splitEntries.length > 1
            && newEntry.breakStartTime
            && newEntry.startTime
          ) {
            selectedEntryToPass = null;
          }

          const { formattedEntries, newId } = addOrUpdateEntries({
            entries: ourParsedEntries,
            newEntry,
            selectedEntry: selectedEntryToPass,
            userId: taskUserId,
          });
          ourParsedEntries = formattedEntries;
          if (newId) {
            editIds.add(newId);
          } else if (selectedEntry?.id) {
            editIds.add(selectedEntry.id);
          }
        });

        const daysManualEntries = [];
        const daysManualEntryIds = new Set();
        entries.forEach((entry) => {
          if (
            !editIds.has(entry.id)
            && entry.userId === taskUserId
            && !entry.isPending
          ) {
            daysManualEntries.push(entry);
            daysManualEntryIds.add(entry.id);
          }
        });

        const ourTasks = ourTimeEntryUserMap[taskUserId] ?? [];

        ourTasks.forEach((entry) => {
          if (
            !editIds.has(entry.id)
            && !daysManualEntryIds.has(entry.id)
            && !deletedIds.has(entry.id)
          ) {
            daysManualEntries.push(entry);
            daysManualEntryIds.add(entry.id);
          }
        });

        const newTasksForConflict = ourParsedEntries.filter((entry) => editIds.has(entry.id));
        userConflictChecks.push({
          taskUserId,
          editIds,
          daysManualEntries,
          newTasksForConflict,
          ourParsedEntries,
        });
      });

      const tasksForCostcodeLookup = userConflictChecks.flatMap(({
        daysManualEntries,
        newTasksForConflict,
      }) => [...daysManualEntries, ...newTasksForConflict]);

      const resolvedCostcodeIdMap = await dispatch(
        updateCostcodeIdMapForTasks(tasksForCostcodeLookup),
      );

      userConflictChecks.forEach(({
        taskUserId,
        editIds,
        daysManualEntries,
        newTasksForConflict,
        ourParsedEntries,
      }) => {
        const userConflictMessage = foundTaskConflict({
          editIds,
          currentTasks: daysManualEntries,
          newTasks: newTasksForConflict,
          phases,
          projectIdMap,
          userMap,
          costcodeIdMap: resolvedCostcodeIdMap,
          showOriginalTimes: settings?.showOriginalTimes,
          detectHourBasedConflict: settings?.detectHourBasedConflict,
        });

        if (userConflictMessage) conflictMessages.push(userConflictMessage);

        if (selectedEntry && taskUserId === selectedEntry.userId) hasSelectedEntryUser = true;

        parsedEntries = parsedEntries.concat(ourParsedEntries);
      });

      // add previous users entries if the user was not in a new entry/edit
      const prevUsers = new Set(entries.map((entry) => entry.userId));
      const newUsers = new Set(parsedEntries.map((entry) => entry.userId));
      const missingUsers = new Set(Array.from(prevUsers).filter((prevId) => !newUsers.has(prevId)));
      const missingUsersEntries = entries.filter((entry) => missingUsers.has(entry.userId));
      parsedEntries = missingUsersEntries.concat(parsedEntries);

      // remove selected entry if the user was removed from it
      if (selectedEntry && !hasSelectedEntryUser && !isCopying) {
        parsedEntries = parsedEntries.filter((entry) => entry.id !== selectedEntry.id);
      }
    }));

    if (conflictMessages.length) {
      notification.error({
        message: (
          <div>
            {conflictMessages.map((m, idx) => (
              // eslint-disable-next-line react/no-array-index-key
              <div key={idx}>
                {m}
              </div>
            ))}
          </div>
        ),
        getContainer: null,
        placement: 'bottomRight',
        bottom: 50,
        duration: 10,
      });
      return;
    }

    setEntries(parsedEntries);
    setSelectedEntry();
    setIsCopying(false);
    setShowFormDrawer(false);
  }, [
    entries,
    selectedEntry,
    isCopying,
    userMap,
    projectIdMap,
    deletedIds,
    paidFlags,
    getWithinRangeEntriesMap,
    settings,
    phases,
  ]);

  const handleSubmit = useCallback(async (newTasks, hourBased, isMultiDayEntry) => {
    const newEntries = [];
    await Promise.all(newTasks.map(async (newTask) => {
      const { task: editedTask } = newTask;
      const fullEntries = timeEntryUserMap[selectedEntry?.userId] ?? [];
      const breakEntries = fullEntries.filter((task) => (
        task.type === 'break'
        && TaskHelpers.taskIsOnDay(task, TaskHelpers.getTaskDate(editedTask))
        && task.note === 'Automatic Break'
        && !deletedIds.has(task.id)
      ));
      const totalBreakRuntime = breakEntries.reduce((acc, val) => (
        acc + TaskHelpers.getRuntime(val.startTime, val.endTime)
      ), 0);
      if (
        disableAutoBreakEditPrompt
        || !hasEditedTimes(editedTask, selectedEntry)
        || !breakEntries.length
        || (totalBreakRuntime >= TaskHelpers.getRuntime(editedTask))
        || isCopying
      ) {
        newEntries.push(newTask);
        return;
      }

      // eslint-disable-next-line consistent-return
      return new Promise((resolve) => (
        // eslint-disable-next-line no-promise-executor-return
        CustomConfirmModal({
          title: 'Include Auto-Break Time Into Time Edit',
          okText: 'Yes',
          cancelText: 'No',
          content: 'It looks like you\'re editing a time card that already has an automatic break. Would you like to incorporate the break time into this edit?',
          onOk: async () => {
            const newTasksObj = { ...newTask };
            const newEditedTask = { ...editedTask };
            let remainingBreakTimeToSubtract = totalBreakRuntime;

            AUTO_BREAK_INCLUDE_KEYS.forEach(({ endTimeKey, startTimeKey }) => {
              if (remainingBreakTimeToSubtract === 0) return;
              if (newEditedTask[endTimeKey]) {
                const valueStartTime = newEditedTask[startTimeKey];
                const valueEndTime = newEditedTask[endTimeKey];
                const duration = valueEndTime - valueStartTime;

                if (duration <= remainingBreakTimeToSubtract) {
                  newEditedTask[endTimeKey] = valueStartTime;
                  remainingBreakTimeToSubtract -= duration;
                } else {
                  newEditedTask[endTimeKey] = valueEndTime - remainingBreakTimeToSubtract;
                  remainingBreakTimeToSubtract = 0;
                }
              }
            });

            newTasksObj.task = newEditedTask;
            newEntries.push(newTasksObj);
            resolve();
          },
          onCancel: async () => {
            newEntries.push(newTask);
            resolve();
          },
        })
      ));
    }));

    onSubmitEntry(newEntries, hourBased, isMultiDayEntry);
  }, [
    selectedEntry,
    onSubmitEntry,
    timeEntryUserMap,
    deletedIds,
    isCopying,
    disableAutoBreakEditPrompt,
  ]);

  const onDeleteEntry = useCallback((recordId) => {
    setEntries(entries.filter((entry) => entry.id !== recordId));
    const newDeleted = new Set(deletedIds);
    newDeleted.add(recordId);
    setDeletedIds(newDeleted);
  }, [entries, deletedIds]);

  const onClickEntry = useCallback((recordId, mode = 'edit') => {
    const ourEntry = entries.find((entry) => entry.id === recordId);
    if (!ourEntry || ourEntry.isPending) return;
    setSelectedEntry(ourEntry);
    setIsCopying(mode === 'copy');
    setIsRedistributingHours(mode === 'redistribute');
    setShowFormDrawer(true);
  }, [entries]);

  const renderItem = useCallback((task) => (
    <ManualEntryCard
      task={task}
      onDelete={onDeleteEntry}
      onClick={onClickEntry}
      onFileOpen={setSelectedFile}
      onFileDetailChange={setSelectedFileDetails}
      fileMap={fileMap}
      isDisplay={isDisplay}
      onOpenLocation={setLocationDrawerConfig}
      userMap={userMap}
      projectIdMap={projectIdMap}
      equipmentIdMap={equipmentIdMap}
      settings={settings}
    />
  ), [onDeleteEntry, onClickEntry, fileMap, isDisplay, projectIdMap, equipmentIdMap, settings]);

  const onDownload = useCallback(() => {
    if (!globalFile) return;
    const params = globalFile instanceof File
      ? { fileObject: globalFile }
      : { fileURL: globalFile.url };
    downloadFile(params);
  }, [globalFile]);

  const onNext = useCallback((isRight) => () => onNextFileClick({
    isRight,
    selectedFileDetails,
    fileMap,
    globalFileMap,
    setSelectedFile,
    setSelectedFileDetails,
    dispatch,
  }), [selectedFileDetails, fileMap, globalFileMap, setSelectedFile, setSelectedFileDetails]);
  const classMap = useMemo(() => getIdMap(classes), [classes]);
  const defaultSageShiftId = useMemo(() => {
    if (personal && profile && profile.defaultSageShiftId) return profile.defaultSageShiftId;
    if (tasks && tasks.length > 0) {
      // For someone else
      const { sageShiftId: lastTaskSageShiftId } = tasks.length > 0 ? tasks[tasks.length - 1] : {};
      return lastTaskSageShiftId;
    }
    return null;
  }, [personal, tasks, profile]);

  useEffect(() => {
    const load = async () => {
      try {
        const taskIds = initialEntries.map((task) => task.id).filter((task) => !task.isPending);
        const {
          dataMap: newCustomDataMap,
          fileMap: newFileMap,
        } = await loadManualEntryData(taskIds, true);

        const seen = new Set();
        const fullTasks = initialEntries.map((entry) => {
          const { id: entryId, isPending } = entry;
          const taskObj = {
            ...entry,
          };

          if (!isPending) {
            const {
              [entryId]: {
                data: initialData = [],
                divisionId,
              } = {},
            } = newCustomDataMap;
            taskObj.customData = initialData;
            taskObj.divisionId = divisionId;
          }

          return taskObj;
        }).filter((entry) => (
          !!TaskHelpers.getEndTime(entry)
          && (
            !entry.id || (!seen.has(entry.id) && seen.add(entry.id)
            )
          )
        ));
        const parsedFileMap = {};
        mergeDecoratedFiles(parsedFileMap, newFileMap);
        // Pending entries have no task row, so their files come with the entry itself
        fullTasks.forEach(({ customDataFileMap }) => {
          mergeDecoratedFiles(parsedFileMap, customDataFileMap);
        });
        setEntries(fullTasks);
        setFileMap(parsedFileMap);
        setCustomDataMap(newCustomDataMap);
      } catch (err) {
        message.error('Failed to load timecard custom fields');
      }
      setCustomDataLoading(false);
      setHasLoadedCustomData(true);
    };
    if (visible && !hasLoadedCustomData) {
      setCustomDataLoading(true);
      setTimeout(() => {
        load();
      }, 250);
    }
  }, [
    visible,
    initialEntries,
    defaultClassId,
    classMap,
    enableHourBasedTracking,
    customFields,
    isSummary,
  ]);

  useEffect(() => {
    if (visible
      && !hasLoadedSummaryTask
      && hasLoadedCustomData
      && isSummary
      && entries.length
      && !isDisplay
      && selectedSummaryTask?.id
    ) {
      const ourEntry = entries.find((e) => e.id === selectedSummaryTask.id);
      setHasLoadedSummaryTask(true);
      if (!ourEntry || ourEntry.isPending) return;
      setSelectedEntry(ourEntry);
      setShowFormDrawer(true);
    }
  }, [visible, hasLoadedCustomData, isSummary, entries, isDisplay, selectedSummaryTask]);

  useEffect(() => {
    const load = async () => {
      setInitialLoaded(false);
      await Promise.all([
        dispatch(getCustomFields()),
        !isSummary && dispatch(getUsers()),
        dispatch(getCustomers()),
        dispatch(getCustomerLabels()),
        dispatch(getMaterials()),
        dispatch(getEquipment()),
        dispatch(getCustomTables()),
      ]);
      setInitialLoaded(true);
    };
    if (visible) {
      load();
    } else {
      setFileMap({});
      setEntries([]);
      setHasLoadedCustomData(false);
      setHasLoadedSummaryTask(false);
      setDeletedIds(new Set());
    }
  }, [dispatch, visible, isSummary]);

  useEffect(() => {
    const getSignedURL = async () => {
      const signedTask = entries.find((t) => t.signatureReferenceId);
      if (signedTask) {
        const signedURL = await dispatch(getSignatureURL(signedTask));
        setSignatureURL(signedURL);
      }
    };
    if (entries && entries.length) {
      getSignedURL();
    } else {
      setSignatureURL();
    }
  }, [entries]);

  const listHeight = signatureUrl ? 'calc(100% - 100px)' : '100%';

  const canEdit = canEditApproved || canEditUnapproved;

  const dataLoading = useMemo(() => (customDataLoading || !mainLoaded || !initialLoaded), [
    customDataLoading, mainLoaded, initialLoaded,
  ]);

  useEffect(() => {
    if (setIsLoading) {
      setIsLoading(dataLoading || loading);
    }
  }, [setIsLoading, dataLoading, loading]);

  const style = dataLoading
    ? {
      position: 'absolute',
      top: '115px',
      bottom: '0px',
      width: '100%',
    } : {
      padding: 0,
      paddingBottom: isDisplay && !canEdit ? 0 : FOOTER_HEIGHT,
    };

  const numEntryFiles = (
    selectedFileDetails?.response?.fileIds?.length ?? selectedFileDetails?.response?.files?.length
  );

  const formDrawerEntries = useMemo(() => entries.filter((e) => !e.isPending), [entries]);

  const hourRedistributeEntries = useMemo(() => {
    if (!selectedEntry) return [];
    return entries?.filter((e) => (
      (e.groupId && selectedEntry.groupId && e.groupId === selectedEntry.groupId)
      || e.id === selectedEntry.id
    ));
  }, [entries, selectedEntry]);

  return (
    <div style={style}>
      {
        (dataLoading)
          ? <Row justify="center" align="middle" style={{ height: '100%' }}><Col><Spin /></Col></Row>
          : (
            <List
              dataSource={entries}
              renderItem={renderItem}
              rowKey={(item) => item.id}
              style={{ height: listHeight, overflow: 'auto' }}
            />
          )
      }
      <ManualEntrySignature
        signatureUrl={signatureUrl}
      />
      <ManualEntryFooter
        isDisplay={isDisplay}
        canEdit={canEdit}
        onEditClicked={onEditClicked}
        loading={loading}
        onSubmit={onSubmit}
        onClose={onClose}
        day={day}
        isApprovals={isApprovals}
        userId={userId}
        customDataLoading={dataLoading}
      />
      <ManualEntryFormDrawer
        visible={showFormDrawer}
        onClose={hideFormDrawer}
        onSubmit={handleSubmit}
        defaultClassId={defaultClassId}
        defaultSageShiftId={defaultSageShiftId}
        selectedEntry={selectedEntry}
        defaultDate={defaultDate}
        fileMap={fileMap}
        userId={userId}
        entries={formDrawerEntries}
        user={user}
        allowInlineTimes
        shouldRoundTimes
      />
      <HoursRedistributionDrawer
        visible={isRedistributingHours}
        onClose={hideRedistributionDrawer}
        entry={selectedEntry}
        initialEntries={hourRedistributeEntries}
        setEntries={setEntries}
        customDataMap={customDataMap}
      />
      <ManualEntryLocation
        visible={!!locationDrawerConfig}
        options={locationDrawerConfig}
        onClose={onCloseLocationDrawer}
      />
      <FullPhoto
        type={selectedFile ? getFileType({ name: selectedFile.name }) : null}
        url={selectedFile ? selectedFile.url : null}
        title={selectedFile ? selectedFile.name : null}
        file={globalFile && globalFile instanceof File ? globalFile : null}
        onClose={onFileClose}
        onDownload={onDownload}
        onLeft={onNext(false)}
        onRight={onNext(true)}
        showLeft={selectedFile && selectedFileDetails?.index > 0}
        showRight={selectedFile && selectedFileDetails?.index < (numEntryFiles - 1)}
        useApryse
      />
    </div>
  );
}

/* eslint-disable react/forbid-prop-types */
ManualEntry.propTypes = {
  user: PropTypes.object,
  onClose: PropTypes.func.isRequired,
  visible: PropTypes.bool,
  personal: PropTypes.bool,
  initialEntries: PropTypes.array,
  defaultDate: PropTypes.string,
  isDisplay: PropTypes.bool,
  onEditClicked: PropTypes.func.isRequired,
  canEditApproved: PropTypes.bool,
  canEditUnapproved: PropTypes.bool,
  showFormDrawer: PropTypes.bool,
  setShowFormDrawer: PropTypes.func.isRequired,
  day: PropTypes.object,
  isApprovals: PropTypes.bool,
  isSummary: PropTypes.bool,
  updateManualEntryData: PropTypes.func.isRequired,
  isCopying: PropTypes.bool.isRequired,
  setIsCopying: PropTypes.func.isRequired,
  setIsLoading: PropTypes.func.isRequired,
  isRedistributingHours: PropTypes.bool,
  setIsRedistributingHours: PropTypes.func,
  selectedSummaryTask: PropTypes.shape({
    id: PropTypes.string,
  }),
};

ManualEntry.defaultProps = {
  user: {},
  visible: false,
  personal: false,
  initialEntries: [],
  defaultDate: DateTime.local().toSQLDate(),
  isDisplay: false,
  canEditApproved: false,
  canEditUnapproved: false,
  showFormDrawer: false,
  day: {},
  isApprovals: false,
  isSummary: false,
  isRedistributingHours: false,
  setIsRedistributingHours: () => {},
  selectedSummaryTask: null,
};

export default ManualEntry;
